import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";

import {
  ContainerValue,
  ContainerView,
  FlexBox,
  CorrectPullRefresh,
  PullGrid,
} from "components/containers";
import {
  Button,
  TitleH1,
  TitleH18,
  TitleH3,
  TitleH4,
} from "components/generic";
import { MenuLeftSide } from "components/MenuLeftSide";
import { ButtonsDiv, DivBlock, MainWrapper } from "./styles";
import {
  IMatchesEnum,
  IStudentStripeMatches,
  THostShot,
  THouseAddress,
} from "interfaces/intarfaces";
import { IcoCalendar } from "components/InputCalendar/styles";
import { LoaderCustom } from "components/Loader";
import { theme } from "styles/theme";
import HowItWork from "components/HowItWork";
import {
  DateLine,
  NoMatchesInfoSection,
  UnauthorizedUser,
} from "pages/ProfileUser/SimpleComponents";
import { StudentAPI } from "API/studentApi";
import { UserAPI } from "API/userApi";
import {
  API_HOUSES,
  API_MATCHES,
  API_USERS,
  matchStatus,
  STUDENT_CHATS_ROUTE,
  STUDENT_ROOMINFO_ROUTE,
} from "utils/constants";
import { useContextHospi } from "context/ContextHospi";
import { getNameCountry } from "utils/fetchMapInfo";
import { HelpWidget } from "components/HelpWidget";
import { HouseAPI } from "API/houseApi";
import { filterMatches } from "utils/helpers";
import { TLang } from "assets/data/isoNameLanguage";

const StudentMatches: React.FC = () => {
  const [listOffers, setListOffers] = useState<IStudentStripeMatches[]>([]);
  const { t } = useTranslation("matches");
  const [loading, setLoading] = useState(false);
  const { userProfile } = useContextHospi();
  const [response401, setResponse401] = useState(false);

  const nameProps = (status: number, mode: "in" | "out"): keyof IMatchesEnum =>
    status === matchStatus.INVITED
      ? `offered_check_${mode}_date`
      : `check_${mode}_date`;

  const fetchData = () => {
    StudentAPI.getMatches(
      API_MATCHES + `student-matches/${userProfile?.user_id}`
    )
      .then((response: IMatchesEnum[]) => {
        // If bad request
        if (
          response.hasOwnProperty("status") ||
          response.hasOwnProperty("isAxiosError")
        ) {
          throw new Error("Something went wrong, 102");
        }
        // If we got success response
        else if (response.length) {
          let hostCache = new Map<string, THostShot>();
          let houseCache = new Map<string, THouseAddress>();
          const arrayHostID: string[] = [];
          const arrayHouseID: string[] = [];
          response.forEach((item: IMatchesEnum) => {
            !arrayHostID.includes(item.host_id) &&
              arrayHostID.push(item.host_id);
            !arrayHouseID.includes(item.house_id) &&
              arrayHouseID.push(item.house_id);
          });
          Promise.allSettled([
            ...arrayHostID.map((item) =>
              UserAPI.get(API_USERS + item + "/user-preview")
            ),
            ...arrayHouseID.map((item) => HouseAPI.get(API_HOUSES + item)),
          ]).then((results: PromiseSettledResult<any>[]) => {
            results.forEach(
              (
                item: PromiseSettledResult<THostShot | THouseAddress>,
                num: number
              ) => {
                item.status === "fulfilled" &&
                  (num < arrayHostID.length
                    ? hostCache.set(
                      (item.value as THostShot).user_id,
                      item.value as THostShot
                    )
                    : houseCache.set(
                      (item.value as THouseAddress).house_id!,
                      item.value as THouseAddress
                    ));
              }
            );
            const tempData: IStudentStripeMatches[] = [];

            response.forEach((item: IMatchesEnum, num: number) => {
              if (
                hostCache.get(item.host_id) !== undefined &&
                houseCache.get(item.house_id) !== undefined
              )
                if (response[num].match_status! > matchStatus.CHATTED)
                  tempData.push({
                    host: hostCache.get(item.host_id)!,
                    address: houseCache.get(item.house_id)!,
                    period: {
                      start:
                        (response[num][
                          nameProps(response[num].match_status!, "in")
                        ] as string) ?? userProfile?.preferred_move_in,
                      end:
                        (response[num][
                          nameProps(response[num].match_status!, "out")
                        ] as string) ?? userProfile?.preferred_move_out,
                      currently:
                        response[num].match_status! > matchStatus.INVITED,
                    },
                  });
            });
            setListOffers(() => tempData.sort(filterMatches));
          });
        }
        setLoading(false);
      })
      .catch(() => {
        setListOffers([]);
        setLoading(false);
      });
  };
  useEffect(() => {
    setLoading(true);
    fetchData();
    // eslint-disable-next-line
  }, [userProfile]);

  const handleOffer = (
    offer_data: Pick<IMatchesEnum, "house_id" | "host_id">,
    mode: boolean
  ) => {
    setLoading(true);
    StudentAPI.post(API_MATCHES + `${mode ? "accept" : "reject"}-invite`, {
      ...offer_data,
      student_id: userProfile?.user_id || "",
    })
      .then((response) => {
        if (response.hasOwnProperty("status")) {
          setResponse401(response.status === 401);
        } else {
          if (mode) {
            setListOffers((prev) =>
              prev.map((item) => {
                if (item.address.house_id === offer_data.house_id)
                  return {
                    ...item,
                    period: {
                      ...item.period,
                      currently: mode,
                    },
                  };
                return item;
              })
            );
          } else {
            setListOffers((prev) =>
              prev.filter((item) => item.address.house_id !== offer_data.house_id)
            );
          }
        }
      })
      .catch()
      .finally(() => setLoading(false));
  };

  // When user Unauthorized
  if (response401) return <UnauthorizedUser />;

  return (
    <ContainerView>
      <MenuLeftSide />
      <MainWrapper style={{ paddingBottom: "1rem" }}>
        {loading && (
          <LoaderCustom
            size={5}
            color={theme.colors.bgBody}
            text={t("loading")}
          />
        )}
        <CorrectPullRefresh>
          <PullToRefresh onRefresh={async () => fetchData()}>
            <PullGrid>
              <TitleH1>{t("student.title")}</TitleH1>
              <HowItWork
                title={t("butHowItWork")}
                items={[t("student.firstDesc")]}
              />
              {!!!listOffers.length && <NoMatchesInfoSection />}
              {!!listOffers.length &&
                listOffers
                  .filter((item) => !item.period.currently)
                  .map((item: IStudentStripeMatches, i: number) => (
                    <MatchCardStudent
                      {...item}
                      key={i}
                      handleOffer={handleOffer}
                    />
                  ))}
              {!!listOffers.filter((item) => item.period.currently).length && (
                <TitleH3 style={{ justifySelf: "flex-start" }}>
                  {t("student.livingHere")}
                </TitleH3>
              )}
              {!!listOffers.length &&
                listOffers
                  .filter((item) => item.period.currently)
                  .map((item: IStudentStripeMatches, i: number) => (
                    <MatchCardStudent {...item} key={i} />
                  ))}
              <HelpWidget
                title={t(`student.help.title`)}
                text={[t(`student.help.text`)]}
                btnText={t(`student.help.btnText`)}
              />
            </PullGrid>
          </PullToRefresh>
        </CorrectPullRefresh>
      </MainWrapper>
    </ContainerView>
  );
};

export default StudentMatches;

const MatchCardStudent: React.FC<
  IStudentStripeMatches & {
    handleOffer?: (
      offer_data: Pick<IMatchesEnum, "house_id" | "host_id">,
      mode: boolean
    ) => void;
  }
> = ({ host, address, period, handleOffer }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("matches");

  const handleInvite = (isGet: boolean) => {
    handleOffer &&
      handleOffer(
        {
          host_id: host.user_id,
          house_id: address.house_id!,
        },
        isGet
      );
  };

  const currLang = i18n.language.toUpperCase() as TLang
  return (
    <ContainerValue style={{ padding: "1rem", margin: 0 }}>
      {!period.currently && (
        <>
          <TitleH3>{`${host.first_name} ${host.last_name} ${t(
            "student.invitation"
          )}`}</TitleH3>
          <TitleH4 m="0 0 8px 0">{t("student.secondDesc")}</TitleH4>
        </>
      )}
      <FlexBox justify="flex-start" align="center">
        <FlexBox justify="flex-start">
          <IcoCalendar
            size="1.1em"
            fontColor
            style={{ margin: "0 8px 0 0", cursor: "default" }}
          />
          <DateLine start={period.start} end={period.end} />
        </FlexBox>
        {!period.currently && (
          <div className="buttonGroup">
            <Button normal m="0 16px 0 0" onClick={() => handleInvite(false)}>
              {t("student.butDecline")}
            </Button>
            <Button main onClick={() => handleInvite(true)}>
              {t("student.butAccept")}
            </Button>
          </div>
        )}
      </FlexBox>
      <hr />
      <FlexBox justify="flex-start" align="flex-start">
        <img src={host.thumbnail_url + `?${new Date().toString()}`} alt="Profile" style={{ paddingRight: "16px" }} />
        <DivBlock>
          <TitleH18>{`${host.first_name} ${host.last_name}`}</TitleH18>
          <TitleH3>{`${address.street} ${address.house_number} ${address.additional_house_number}`}</TitleH3>
          <TitleH4>{`${address.postal_code} ${address.province}`}</TitleH4>
          <TitleH4>{`${getNameCountry(address.country, currLang)}`}</TitleH4>
        </DivBlock>
      </FlexBox>
      <ButtonsDiv>
        <Button
          outline
          onClick={() =>
              navigate(`/${i18n.language}${STUDENT_ROOMINFO_ROUTE.slice(5)}` + `/${address.house_id}`)
          }
        >
          {t("student.butInfo")}
        </Button>
        <Button
          normal
          onClick={() =>
              navigate(
              `/${i18n.language}${STUDENT_CHATS_ROUTE.slice(5)}` + `/${host.user_id}/${address.house_id}`
            )
          }
        >
          {t("student.butChat")}
        </Button>
      </ButtonsDiv>
    </ContainerValue>
  );
};
